import React, { useState } from "react";
import { useGov } from "@/hooks/governance";
import { unixToDate } from "@/lib/format";
import { Badge, HashCell } from "@/components/primitives";
import { Table } from "@/components/Table";
import {
  GovPageShell, GovCard, GovRow, GovSection, GovError, GovSkeleton, GovEmpty,
  GovTabs, GovPager, useGovNav,
} from "@/components/governance/GovShell";
import { entityVoteColumns } from "@/components/governance/GovParts";
import { CommitteeShare } from "@/components/governance/CommitteeShare";

const PAGE = 25;

const Votes = ({ coldId, navigate }) => {
  const [page, setPage] = useState(1);
  const { data, error, loading, refetch } = useGov(`/governance/committee/members/${coldId}/votes`, { params: { count: PAGE, page, order: "desc" } });
  const rows = Array.isArray(data) ? data : [];
  if (error) return <GovError error={error} onRetry={refetch} />;
  return (
    <>
      <Table columns={entityVoteColumns(navigate)} rows={rows} loading={loading} skeletonRows={8} empty="This member has not voted." />
      <GovPager page={page} onPage={setPage} hasNext={rows.length === PAGE} loading={loading} />
    </>
  );
};

export const CommitteeMember = ({ meta, coldId }) => {
  const navigate = useGovNav();
  const { data: d, error, loading, refetch } = useGov(`/governance/committee/members/${coldId}`);
  const [tab, setTab] = useState("profile");
  const memberships = Array.isArray(d?.memberships) ? d.memberships : [];

  return (
    <GovPageShell crumbs={meta.crumbs} title={meta.title} subtitle={coldId} actions={d ? <CommitteeShare coldId={coldId} d={d} /> : null}>
      {error ? (
        <GovError error={error} onRetry={refetch} />
      ) : loading && !d ? (
        <GovSkeleton lines={6} />
      ) : d ? (
        <>
          <GovTabs
            tabs={[{ key: "profile", label: "Profile" }, { key: "votes", label: "Votes" }]}
            active={tab}
            onChange={setTab}
          />
          <div data-testid={`gov-cc-member-tab-${tab}`}>
            {tab === "profile" ? (
              <div className="space-y-4">
                <GovCard>
                  <GovRow label="Cold ID"><HashCell value={d.cold_id} len={14} /></GovRow>
                  <GovRow label="Cold Hex"><HashCell value={d.cold_hex} len={12} /></GovRow>
                  <GovRow label="Status"><Badge tone={d.status === "active" ? "success" : "default"}>{d.status}</Badge></GovRow>
                  <GovRow label="Current Hot Key">{d.current_hot_id ? <HashCell value={d.current_hot_id} len={12} /> : "—"}</GovRow>
                  <GovRow label="Script Credential">{d.cold_has_script ? <Badge>script</Badge> : <Badge>key</Badge>}</GovRow>
                </GovCard>
                <GovSection title="Memberships">
                  <Table
                    columns={[
                      { key: "id", label: "Committee", render: (r) => <span className="text-foreground">#{r.committee_id}</span> },
                      { key: "from", label: "From", render: (r) => <span className="text-muted-foreground">{r.effective_from_epoch != null ? `epoch ${r.effective_from_epoch}` : "not enacted"}</span> },
                      { key: "to", label: "To", render: (r) => <span className="text-muted-foreground">{r.current ? "current" : (r.effective_to_epoch ? `epoch ${r.effective_to_epoch}` : "—")}</span> },
                      { key: "exp", label: "Expires", align: "right", render: (r) => <span className="text-muted-foreground">{r.expiration_epoch ? `epoch ${r.expiration_epoch}` : "—"}</span> },
                      { key: "current", label: "Active", align: "right", render: (r) => (r.current ? <Badge tone="success">yes</Badge> : <Badge>no</Badge>) },
                    ]}
                    rows={memberships}
                    empty="No memberships."
                  />
                </GovSection>
              </div>
            ) : (
              <Votes coldId={coldId} navigate={navigate} />
            )}
          </div>
        </>
      ) : <GovEmpty />}
    </GovPageShell>
  );
};
